import React, { useEffect, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { toast } from "react-toastify";

import Header from "../components/Header";

import { createUser, deleteUser, getUser } from "../auth/Auth";
import { postRequest, uploadFile } from "../routes/Routes";
import { loadClientUsers } from "../common/Common";

import avatar from "../assets/images/avatar.png";

import '../assets/css/ManageAdmins.css';

function AccountSettings({ selectedClient }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({});
    const [success, setSuccess] = useState({});
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState({});
    const [passwordSuccess, setPasswordSuccess] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const uploadProfileImageInput = React.createRef();

    useEffect(() => {
        getUser().then((data) => {
            setUser(data);
        }).catch(() => null);
    }, []);

    useEffect(() => {
        const updateMedia = () => setIsMobile(window.innerWidth < 768);
        updateMedia();
        window.addEventListener('resize', updateMedia);
        return () => window.removeEventListener('resize', updateMedia);
    }, []);

    const formSubmit = (event) => {
        setLoading(true);
        event.preventDefault();
        let values = {
            dto: {
                email: event.target.email.value,
                firstName: event.target.firstName.value,
                lastName: event.target.lastName.value,
                address: event.target.address.value,
            },
            context: {
                action: 'Update Account'
            },
            endpoint: '/api/user/update',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                setUser({ ...response.data });
                response.data = { ...response.data, token: user.token };
                createUser(response.data).then(() => {
                    window.location.href = '/';
                });
                setError(values => ({ ...values, error: false, message: null }));
                setSuccess(values => ({ ...values, success: true, message: 'Successfully updated user' }));
            } else {
                setError(values => ({
                    ...values,
                    error: true,
                    errorMessage: response.data.message ? response.data.message : "An error occurred!"
                }));
            }
            setLoading(false);
        });
    }

    const passwordSubmit = (event) => {
        setPasswordLoading(true);
        event.preventDefault();
        if (event.target.newPassword.value !== event.target.confirmPassword.value) {
            setPasswordError(values => ({ ...values, error: true, errorMessage: "Passwords mismatching!" }));
            setPasswordLoading(false);
            return;
        }
        let values = {
            dto: {
                email: user.email,
                oldPassword: event.target.password.value,
                newPassword: event.target.newPassword.value,
            },
            context: {
                action: 'Update Account'
            },
            endpoint: '/api/user/update/password',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                setUser({ ...response.data });
                response.data = { ...response.data, token: user.token };
                setPasswordError(values => ({ ...values, error: false, message: null }));
                setPasswordSuccess(values => ({ ...values, success: true, message: 'Successfully updated password' }));
                deleteUser().then(() => {
                    setUser(null);
                    window.location.href = '/login';
                });
            } else {
                setPasswordError(values => ({
                    ...values,
                    error: true,
                    errorMessage: response.data.message ? response.data.message : "An error occurred!"
                }));
            }
            setPasswordLoading(false);
        });
    }

    const uploadProfileImage = (event) => {
        event.preventDefault();
        const data = new FormData();
        data.append('file', uploadProfileImageInput.current.files[0]);
        uploadFile(data, '/api/user/upload?user=' + user.email, { token: user.token }).then(response => {
            if (response.status !== 200) {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
            toast.success("Successfully uploaded profile image", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setUser({ ...response.data });
            response.data = { ...response.data, token: user.token };
            createUser(response.data).then(() => {
                window.location.href = '/';
            });
        });
    }

    if (user) {
        return (
            <>
                <Header component="Account Settings" />
                <div className="grey-background full-height">
                    <div className="container">
                        <div className="d-flex name-container">
                            <div className="p-2">Account Settings</div>
                            <div className="p-2">
                                <ScrollContainer>
                                    <div className="name-list-container" style={{ width: "100%" }}>
                                        <div onClick={() => {
                                            setError(values => ({ ...values, error: false, message: null }));
                                            setSuccess(values => ({ ...values, success: false, message: null }));
                                        }} className='active-user'>
                                            {user.firstName} {user.lastName}
                                            <hr className="active-hr" />
                                        </div>
                                    </div>
                                </ScrollContainer>
                            </div>
                        </div>
                        <div className="admin-detail-container">
                            <div className="d-flex admin-image-container">
                                <div>
                                    {user.imageUrl ?
                                        <img src={user.imageUrl} alt={user.firstName} className='user-image' />
                                        : <img src={avatar} alt="Avatar" className='user-image' />
                                    }
                                </div>
                                <div className="d-flex">
                                    <div className="admin-name">
                                        {user.firstName + ' ' + user.lastName}
                                        <p>
                                            PNG or JPG no bigger than 1000px wide and tall.
                                        </p>
                                    </div>
                                    <div>
                                        <button className='borderless-button' data-bs-toggle="modal"
                                            data-bs-target="#accountSettingProfileImageModal">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                fill="currentColor"
                                                className="bi bi-upload" viewBox="0 0 16 16">
                                                <path
                                                    d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                                                <path
                                                    d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
                                            </svg>
                                            Upload
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                <div className="'d-flex">
                                    <form onSubmit={formSubmit} className="admin-form-container">
                                        <h3 className="form-heading">Account Information</h3>

                                        {!isMobile ? (
                                            <div className="d-flex desktop-user-form">
                                                <div>
                                                    <div className="form-group">
                                                        <label htmlFor="firstName">First Name</label>
                                                        <input type="text" className="form-control" name="firstName"
                                                            value={user.firstName}
                                                            onChange={(e) =>
                                                                setUser((prev) => ({ ...prev, firstName: e.target.value }))
                                                            }
                                                            placeholder="Type first name"
                                                            required />
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="email">Email</label>
                                                        <input type="email" className="form-control" name="email"
                                                            disabled={true}
                                                            value={user.email}
                                                            placeholder="Type email"
                                                            required />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="form-group">
                                                        <label htmlFor="lastName">Last Name</label>
                                                        <input type="text" className="form-control" name="lastName"
                                                            value={user.lastName}
                                                            onChange={(e) =>
                                                                setUser((prev) => ({ ...prev, lastName: e.target.value }))
                                                            }
                                                            placeholder="Type last name"
                                                            required />
                                                    </div>
                                                    <div className="form-group">
                                                        <label htmlFor="address">Address</label>
                                                        <input type="text" className="form-control" name="address"
                                                            value={user.address}
                                                            placeholder="Type address"
                                                            required />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mobile-user-form">
                                                <div className="form-group">
                                                    <label htmlFor="firstName">First Name</label>
                                                    <input type="text" className="form-control" name="firstName"
                                                        value={user.firstName}
                                                        placeholder="Type first name"
                                                        required />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="lastName">Last Name</label>
                                                    <input type="text" className="form-control" name="lastName"
                                                        value={user.lastName}
                                                        placeholder="Type last name"
                                                        required />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="email">Email</label>
                                                    <input type="email" className="form-control" name="email"
                                                        disabled={true}
                                                        value={user.email}
                                                        placeholder="Type email"
                                                        required />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="address">Address</label>
                                                    <input type="text" className="form-control" name="address"
                                                        value={user.address}
                                                        placeholder="Type address"
                                                        required />
                                                </div>
                                            </div>
                                        )}
                                        {success.success &&
                                            <div className="alert alert-success" role="alert">
                                                {success.message}
                                            </div>}
                                        {error.error &&
                                            <div className="alert login-alert alert-danger" role="alert">
                                                {error.errorMessage}
                                            </div>}
                                        <div className="d-flex admin-form-button-container">
                                            <div className="invite-link-container" />
                                            <div className="admin-form-submit-button-container">
                                                <button type="submit" className='black-button' disabled={loading}>
                                                    {loading ?
                                                        <span className="spinner-grow spinner-grow-sm ml-1" role="status"
                                                            aria-hidden="true" />
                                                        : null
                                                    }
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            }
                            <div className="'d-flex">
                                <form onSubmit={passwordSubmit} className="admin-form-container">
                                    {(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                        <h3 className="form-heading">Update Password</h3>
                                    }
                                    {!isMobile ? (
                                        <div className="d-flex desktop-user-form">
                                            {(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                                <div>
                                                    <div className="form-group">
                                                        <label htmlFor="password">Old Password</label>
                                                        <input type="password" className="form-control" name="password"
                                                            placeholder="Type old password"
                                                            required />
                                                    </div>
                                                </div>
                                            }
                                            {!(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                                <div>
                                                    <>
                                                        <h3 className="form-heading">Account Information</h3>
                                                        <h3 className="form-heading" style={{ marginTop: "5%", fontSize: 20 }}>
                                                            {user.firstName} {user.lastName}
                                                        </h3>
                                                        <p>{user.email}</p>
                                                        <p>{user.addressLineOne}</p>
                                                        <p>{user.addressLineTwo}</p>
                                                        <p>{user.city}</p>
                                                        <p>{user.postcode}</p>

                                                        {selectedClient && (
                                                            <div>
                                                                <h3 className="form-heading mt-5">Company Details</h3>
                                                                <h3 className="form-heading" style={{ marginTop: "5%", fontSize: 20 }}>
                                                                    {selectedClient.name}
                                                                </h3>
                                                                <p>{selectedClient.email}</p>
                                                                <p>{selectedClient.addressLineOne}</p>
                                                                <p>{selectedClient.addressLineTwo}</p>
                                                                <p>{selectedClient.city}</p>
                                                                <p>{selectedClient.postcode}</p>
                                                            </div>
                                                        )}
                                                    </>
                                                </div>
                                            }
                                            <div>
                                                {!(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                                    <>
                                                        <h3 className="form-heading">Change Password</h3>
                                                        <div className="form-group">
                                                            <label htmlFor="password">Old Password</label>
                                                            <input type="password" className="form-control" name="password"
                                                                placeholder="Type old password"
                                                                required />
                                                        </div>
                                                    </>
                                                }
                                                <div className="form-group">
                                                    <label htmlFor="newPassword">New Password</label>
                                                    <input type="password" className="form-control" name="newPassword"
                                                        placeholder="Type new password"
                                                        required />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="confirmPassword">Confirm Password</label>
                                                    <input type="password" className="form-control" name="confirmPassword"
                                                        placeholder="Type confirm password"
                                                        required />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mobile-user-form">
                                            {!(user.role.role === 'ROOT' || user.role.role === 'ADMIN') &&
                                                <>
                                                    <div>
                                                        <>
                                                            <h3 className="form-heading">Account Information</h3>
                                                            <h3 className="form-heading" style={{ marginTop: "5%", fontSize: 20 }}>
                                                                {user.firstName} {user.lastName}
                                                            </h3>
                                                            <p>{user.email}</p>
                                                            <p>{user.addressLineOne}</p>
                                                            <p>{user.addressLineTwo}</p>
                                                            <p>{user.city}</p>
                                                            <p>{user.postcode}</p>

                                                            {selectedClient && (
                                                                <div>
                                                                    <h3 className="form-heading mt-5">Company Details</h3>
                                                                    <h3 className="form-heading" style={{ marginTop: "5%", fontSize: 20 }}>
                                                                        {selectedClient.name}
                                                                    </h3>
                                                                    <p>{selectedClient.email}</p>
                                                                    <p>{selectedClient.addressLineOne}</p>
                                                                    <p>{selectedClient.addressLineTwo}</p>
                                                                    <p>{selectedClient.city}</p>
                                                                    <p>{selectedClient.postcode}</p>
                                                                </div>
                                                            )}
                                                        </>
                                                    </div>
                                                    <h3 className="form-heading">Change Password</h3>
                                                </>
                                            }
                                            <div className="form-group">
                                                <label htmlFor="password">Old Password</label>
                                                <input type="password" className="form-control" name="password"
                                                    placeholder="Type old password"
                                                    required />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="newPassword">New Password</label>
                                                <input type="password" className="form-control" name="newPassword"
                                                    placeholder="Type new password"
                                                    required />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="confirmPassword">Confirm Password</label>
                                                <input type="password" className="form-control" name="confirmPassword"
                                                    placeholder="Type confirm password"
                                                    required />
                                            </div>
                                        </div>
                                    )}
                                    {passwordSuccess.success &&
                                        <div className="alert alert-success" role="alert">
                                            {passwordSuccess.message}
                                        </div>}
                                    {passwordError.error &&
                                        <div className="alert login-alert alert-danger" role="alert">
                                            {passwordError.errorMessage}
                                        </div>}
                                    <div className="d-flex admin-form-button-container">
                                        <div className="invite-link-container" />
                                        <div className="admin-form-submit-button-container">
                                            <button type="submit" className='black-button' disabled={passwordLoading}>
                                                {passwordLoading ?
                                                    <span className="spinner-grow spinner-grow-sm ml-1" role="status"
                                                        aria-hidden="true" />
                                                    : null
                                                }
                                                Update
                                            </button>
                                        </div>
                                    </div>
                                </form>

                            </div>
                        </div>
                    </div>
                    <div className="modal fade" id="accountSettingProfileImageModal" tabIndex="-1"
                        aria-labelledby="accountSettingProfileImageModalLabel"
                        aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h1 className="modal-title fs-5 modelfile-title" id="accountSettingProfileImageModalLabel">
                                        Upload New Profile Image
                                    </h1>
                                    {/* <button type="button" className="btn-close" data-bs-dismiss="modal"
                                            aria-label="Close"/> */}
                                    <button type="button" className="close-btn" data-bs-dismiss="modal" aria-label="Close">
                                        <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g id="vuesax/bold/close-circle">
                                                <g id="close-circle">
                                                    <path id="Vector" d="M13.9997 3.18311C7.57134 3.18311 2.33301 8.42144 2.33301 14.8498C2.33301 21.2781 7.57134 26.5164 13.9997 26.5164C20.428 26.5164 25.6663 21.2781 25.6663 14.8498C25.6663 8.42144 20.428 3.18311 13.9997 3.18311ZM17.9197 17.5331C18.258 17.8714 18.258 18.4314 17.9197 18.7698C17.7447 18.9448 17.523 19.0264 17.3013 19.0264C17.0797 19.0264 16.858 18.9448 16.683 18.7698L13.9997 16.0864L11.3163 18.7698C11.1413 18.9448 10.9197 19.0264 10.698 19.0264C10.4763 19.0264 10.2547 18.9448 10.0797 18.7698C9.74134 18.4314 9.74134 17.8714 10.0797 17.5331L12.763 14.8498L10.0797 12.1664C9.74134 11.8281 9.74134 11.2681 10.0797 10.9298C10.418 10.5914 10.978 10.5914 11.3163 10.9298L13.9997 13.6131L16.683 10.9298C17.0213 10.5914 17.5813 10.5914 17.9197 10.9298C18.258 11.2681 18.258 11.8281 17.9197 12.1664L15.2363 14.8498L17.9197 17.5331Z" fill="#B2B9C2" />
                                                </g>
                                            </g>
                                        </svg>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={uploadProfileImage}>
                                        <div className="custom-file">
                                            {profileImage ?
                                                <img src={URL.createObjectURL(profileImage)} />
                                                : null
                                            }
                                            <input type="file" className="custom-file-input" accept="image/*" required
                                                id="accountImage" ref={uploadProfileImageInput}
                                                onChange={() => setProfileImage(uploadProfileImageInput.current.files[0])} />
                                            <label className="custom-file-label"
                                                htmlFor="accountImage">
                                                <div className="image-uploader-label">
                                                    Click to upload / Drag and Drop image
                                                </div>
                                            </label>
                                        </div>
                                        <div className="admin-form-submit-button-container modal-button-container">
                                            <button className='blue-button' type="submit">
                                                Upload
                                            </button>
                                            <button type="button" className='black-button' data-bs-dismiss="modal">
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    } else {
        return null;
    }
}

export default AccountSettings;
